import React, { type JSX } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Download } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { CustomTransComponent } from '../../components';

type AddPredictPulseDeviceProps = {
    /** Returns the Add Device form to its empty device-type state. */
    onClose: () => void;
};

/**
 * The wizard URL comes from runtime config, so it can be missing (unset Key Vault
 * secret), a placeholder that was never substituted at pod startup, or an
 * unexpected scheme if the config is ever misconfigured. Only absolute http(s)
 * URLs are safe to hand to window.open, so everything else disables the button.
 */
const isDownloadableUrl = (value: string): boolean => {
    try {
        const { protocol } = new URL(value);

        return protocol === 'http:' || protocol === 'https:';
    } catch {
        return false;
    }
};

/**
 * PredictPulse devices are not created through this form. They are activated by
 * a downloadable desktop wizard, so instead of the device model / name / group
 * fields this panel renders the activation instructions and a download link.
 */
export const AddPredictPulseDevice = ({ onClose }: AddPredictPulseDeviceProps): JSX.Element => {
    const { t } = useTranslation();

    // Trimmed because a Key Vault secret can pick up a stray newline or space,
    // which would otherwise fail URL parsing and disable the button.
    const wizardUrl = (window.APP_CONFIG?.PREDICT_PULSE_ACTIVATION_WIZARD_URL ?? '').trim();
    const isWizardUrlConfigured = isDownloadableUrl(wizardUrl);

    const handleDownloadWizard = (): void => {
        if (!isWizardUrlConfigured) {
            return;
        }
        window.open(wizardUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <>
            <Box
                component="ol"
                data-testid="predict-pulse-instructions"
                sx={{ m: 0, pl: 3, color: 'text.primary', mb: 3 }}
            >
                <Typography
                    component="li"
                    variant="body1"
                    data-testid="predict-pulse-instruction-1"
                    sx={{ mb: 0.5, lineHeight: 1.5 }}
                >
                    <CustomTransComponent translationKey={'ADD_DEVICE_FORM:PREDICT_PULSE_WIZARD_INSTRUCTIONS_1'} />
                </Typography>
                <Typography
                    component="li"
                    variant="body1"
                    data-testid="predict-pulse-instruction-2"
                    sx={{ mb: 0.5, lineHeight: 1.5 }}
                >
                    <CustomTransComponent
                        translationKey={'ADD_DEVICE_FORM:PREDICT_PULSE_WIZARD_INSTRUCTIONS_2'}
                        replace={{
                            email: t('ADD_DEVICE_FORM:EMAIL_PLACEHOLDER'),
                        }}
                    />
                </Typography>
                <Typography
                    component="li"
                    variant="body1"
                    data-testid="predict-pulse-instruction-3"
                    sx={{ lineHeight: 1.5 }}
                >
                    <CustomTransComponent translationKey={'ADD_DEVICE_FORM:PREDICT_PULSE_WIZARD_INSTRUCTIONS_3'} />
                </Typography>
            </Box>
            <Button
                variant={'contained'}
                onClick={handleDownloadWizard}
                disabled={!isWizardUrlConfigured}
                fullWidth
                startIcon={<Download />}
                data-testid="download-predict-pulse-wizard-btn"
                sx={{ textTransform: 'none', mb: 3 }}
            >
                <CustomTransComponent translationKey={'ADD_DEVICE_FORM:DOWNLOAD_PREDICT_PULSE_WIZARD'} />
            </Button>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 3 }}>
                <Button
                    variant={'contained'}
                    onClick={onClose}
                    data-testid="predict-pulse-close-btn"
                    sx={{ textTransform: 'none' }}
                >
                    <CustomTransComponent translationKey={'COMMON:CLOSE'} />
                </Button>
            </Box>
        </>
    );
};
